/**
 * MarketplaceItemCard - Card de produto no marketplace
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Exibição de detalhes do produto
 * - Galeria de imagens com navegação
 * - Sistema de destaque (PIX)
 * - Avaliação de vendedor
 * - Ações de proprietário
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - ReactDOM.createPortal (galeria fullscreen)
 * - Card components (shadcn/ui)
 * - Icons (lucide-react)
 * - Componentes extraídos: EditProductDialog, RateSellerButton, FeatureConfirmDialog, PixPaymentDialog
 */

import React, { useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { MapPin, Clock, Home, Wrench, Package, Zap, Truck, MoreHorizontal } from "lucide-react";
import { useTranslation } from "../translations";
import EditProductDialog from "./EditProductDialog";
import RateSellerButton from "./RateSellerButton";
import { FeatureConfirmDialog, PixPaymentDialog } from "./MarketplaceDialogs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MarketplaceItemCard = ({ item, isOwner = false, onUpdate }) => {
  const { t } = useTranslation();
  const [showFeatureDialog, setShowFeatureDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [pixPayment, setPixPayment] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Image navigation functions
  const nextImage = (e) => {
    e.stopPropagation();
    if (item.images && item.images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % item.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (item.images && item.images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + item.images.length) % item.images.length);
    }
  };

  const categoryIcons = {
    propriedades: Home,
    ferramentas: Wrench,
    insumos: Package,
    defensivos: Zap,
    maquinaria: Truck,
    outros: MoreHorizontal
  };

  const IconComponent = categoryIcons[item.category] || Package;

  const handleDelete = async () => {
    if (window.confirm(t('mkConfirmRemoveAd'))) {
      try {
        await axios.delete(`${API}/marketplace/${item.id}`);
        onUpdate();
        alert(t('mkAdRemoved'));
      } catch (error) {
        alert(t('mkErrorRemoveAd'));
      }
    }
  };

  const handleFeature = () => {
    setShowFeatureDialog(true);
  };

  const handleConfirmFeature = async (customerData) => {
    try {
      const paymentData = {
        item_id: item.id,
        amount: 39.90,
        customer_name: customerData.name,
        customer_email: customerData.email,
        customer_document: customerData.document,
        days: 15
      };

      const response = await axios.post(`${API}/marketplace/${item.id}/feature/payment`, paymentData);
      setPixPayment(response.data);
      setShowFeatureDialog(false);
      setShowPaymentDialog(true);
      
      // Start checking payment status
      checkPaymentStatus(response.data.payment_id);
    } catch (error) {
      alert(t('mkErrorCreatePix') + ": " + (error.response?.data?.detail || error.message));
    }
  };

  const checkPaymentStatus = async (paymentId) => {
    try {
      const response = await axios.get(`${API}/marketplace/payments/${paymentId}`);
      setPaymentStatus(response.data);
      
      if (response.data.status === 'paid') {
        alert(t('mkPaymentConfirmed'));
        setShowPaymentDialog(false);
        onUpdate();
      } else if (response.data.status === 'pending') {
        // Check again in 10 seconds
        setTimeout(() => checkPaymentStatus(paymentId), 10000);
      }
    } catch (error) {
      console.error("Erro ao verificar status do pagamento:", error);
    }
  };

  // Calculate if item is featured and active
  const isFeatured = item.featured && item.featured_until && new Date(item.featured_until) > new Date();

  return (
    <Card className={`hevea-product-card ${isFeatured ? 'hevea-featured' : ''}`}>
      
      {/* Product Media with Image Navigation */}
      {item.images && item.images.length > 0 && (
        <div className="relative h-48 overflow-hidden rounded-t-lg group">
          {/* Current image display */}
          {item.images[currentImageIndex]?.includes('.mp4') || item.images[currentImageIndex]?.includes('.mov') || item.images[currentImageIndex]?.includes('video') ? (
            <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
              <video 
                src={item.images[currentImageIndex]} 
                className="w-full h-full object-cover"
                muted
                loop
                onMouseEnter={(e) => e.target.play()}
                onMouseLeave={(e) => e.target.pause()}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-black/50 rounded-full p-3">
                  <Package className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          ) : (
            <img 
              src={item.images[currentImageIndex]} 
              alt={`${item.title} - Foto ${currentImageIndex + 1}`}
              className="w-full h-full object-cover cursor-pointer"
              onClick={() => setShowGallery(true)}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          
          {/* Navigation arrows - show on hover when multiple images */}
          {item.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Imagem anterior"
              >
                ◀
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Próxima imagem"
              >
                ▶
              </button>
            </>
          )}
          
          {/* Image counter and dots indicator */}
          {item.images.length > 1 && (
            <>
              <div className="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs">
                {currentImageIndex + 1}/{item.images.length}
              </div>
              <div className="absolute bottom-2 right-2 flex space-x-1">
                {item.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'
                    }`}
                    aria-label={`${t('mkGoToImage')} ${idx + 1}`}
                  />
                ))}
              </div>
            </>
          )}
          
          {/* Media type indicators */}
          <div className="absolute bottom-2 left-2 flex space-x-1">
            {item.images.some(img => img.includes('video') || img.includes('.mp4') || img.includes('.mov')) && (
              <Badge className="bg-red-500 text-white text-xs">
                📹 {t('mkVideo')}
              </Badge>
            )}
            {item.images.some(img => !img.includes('video') && !img.includes('.mp4') && !img.includes('.mov')) && (
              <Badge className="bg-blue-500 text-white text-xs">
                📸 {t('mkPhotos')}
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Full-screen Gallery Modal - Using React Portal */}
      {showGallery && item.images && item.images.length > 0 && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black/95 flex items-center justify-center"
          style={{ zIndex: 9999 }}
          onClick={() => setShowGallery(false)}
        >
          <button
            onClick={() => setShowGallery(false)}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 bg-black/50 rounded-full w-12 h-12 flex items-center justify-center"
            style={{ zIndex: 10000 }}
          >
            ✕
          </button>
          
          <div className="relative max-w-5xl max-h-[85vh] w-full mx-4 flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={item.images[currentImageIndex]}
              alt={`${item.title} - Foto ${currentImageIndex + 1}`}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
            
            {/* Gallery navigation */}
            {item.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-4 rounded-full text-3xl shadow-lg"
                >
                  ◀
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-4 rounded-full text-3xl shadow-lg"
                >
                  ▶
                </button>
              </>
            )}
            
            {/* Thumbnails */}
            <div className="flex justify-center space-x-3 mt-6">
              {item.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-3 transition-all shadow-md ${
                    idx === currentImageIndex ? 'border-white scale-110 border-4' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt={`${t('mkThumbnail')} ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            
            <div className="text-center text-white mt-3 text-lg font-medium">
              {currentImageIndex + 1} {t('mkOf')} {item.images.length} • {item.title}
            </div>
          </div>
        </div>,
        document.body
      )}
      
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100">
              <IconComponent className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-gray-900">{item.title}</CardTitle>
              <div className="flex flex-wrap gap-2 mt-1">
                {item.is_example && (
                  <Badge className="bg-blue-100 text-blue-700 border border-blue-200">
                    📘 {t('mkExample')}
                  </Badge>
                )}
                <Badge className="bg-green-50 text-green-700 border border-green-200">
                  {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                </Badge>
                {isFeatured && (
                  <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-200">
                    ⭐ {t('mkFeatured')}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-green-600 whitespace-nowrap">
              R$ {item.price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <Badge className={item.condition === 'novo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
              {item.condition.charAt(0).toUpperCase() + item.condition.slice(1)}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-gray-700 mb-2 line-clamp-3">{item.description}</p>
        
        {/* Botão Ver Detalhes */}
        <button 
          type="button"
          className="p-0 h-auto text-green-600 hover:text-green-700 hover:underline mb-4 text-sm font-medium"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowDetails(true);
          }}
        >
          {t('mkSeeFullDetails')} →
        </button>
        
        <div className="space-y-2">
          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="w-4 h-4 mr-2" />
            {item.location}
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Clock className="w-4 h-4 mr-2" />
            {new Date(item.created_at).toLocaleDateString('pt-BR')}
          </div>
          {/* Dias restantes do anúncio */}
          {item.days_remaining !== null && item.days_remaining !== undefined && (
            <div className={`flex items-center text-sm ${item.days_remaining <= 7 ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
              ⏱️ {item.days_remaining} {item.days_remaining === 1 ? t('mkDayRemaining') : t('mkDaysRemaining')}
              {item.days_remaining <= 7 && ' ⚠️'}
            </div>
          )}
        </div>
        
        {/* Informações do Vendedor - Tripé: Segurança + Justiça */}
        {item.seller_info && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-100">
            <p className="text-xs text-green-600 font-medium mb-2">{t('mkiSeller')}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-semibold text-gray-800">{item.seller_info.name}</span>
                {item.seller_info.is_verified && (
                  <span className="text-green-600" title={t('verifiedIdentity')}>✅</span>
                )}
                {item.seller_info.technical_seal && (
                  <span title={`Selo ${item.seller_info.technical_seal}`}>
                    {item.seller_info.technical_seal === 'gold' ? '🥇' : 
                     item.seller_info.technical_seal === 'silver' ? '🥈' : 
                     item.seller_info.technical_seal === 'bronze' ? '🥉' : ''}
                  </span>
                )}
              </div>
              {item.seller_info.rating && (
                <div className="flex items-center text-sm bg-white px-2 py-1 rounded-full border border-gray-200">
                  <span className="text-yellow-500">⭐</span>
                  <span className="ml-1 font-semibold text-gray-800">{item.seller_info.rating.toFixed(1)}</span>
                  <span className="text-gray-400 ml-1 text-xs">({item.seller_info.rating_count})</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm font-medium text-gray-700">{t('mkiContact')}:</p>
          <p className="text-sm text-gray-600">{item.contact_info}</p>
          
          {/* Botão para avaliar vendedor (não-donos) */}
          {!isOwner && item.seller_id && (
            <RateSellerButton sellerId={item.seller_id} sellerName={item.seller_info?.name || t('mkSeller')} itemId={item.id} />
          )}
          
          {isOwner && (
            <div className="flex flex-wrap gap-2 mt-3">
              <EditProductDialog item={item} onUpdate={onUpdate} />
              {!isFeatured && (
                <Button 
                  size="sm" 
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                  onClick={handleFeature}
                >
                  🔥 {t('mkFeature')}
                </Button>
              )}
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handleDelete}
              >
                {t('mkRemove')}
              </Button>
            </div>
          )}
        </div>
      </CardContent>

      {/* PIX Payment Dialogs */}
      {showFeatureDialog && (
        <FeatureConfirmDialog 
          item={item}
          onConfirm={handleConfirmFeature}
          onCancel={() => setShowFeatureDialog(false)}
        />
      )}

      {showPaymentDialog && pixPayment && (
        <PixPaymentDialog 
          payment={pixPayment}
          status={paymentStatus}
          onClose={() => setShowPaymentDialog(false)}
        />
      )}

      {/* Modal de Detalhes Completos */}
      {showDetails && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDetails(false)}
        >
          <div 
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header com imagem */}
            {item.images && item.images.length > 0 && (
              <div className="relative h-64 bg-gray-100">
                <img 
                  src={item.images[0]} 
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                {item.featured && (
                  <Badge className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-red-500 text-white">
                    🔥 Destaque
                  </Badge>
                )}
              </div>
            )}
            
            <div className="p-6">
              {/* Título e Preço */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">{item.title}</h2>
                  <Badge className="mt-2 flex items-center gap-1">
                    <IconComponent className="w-4 h-4" />
                    {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                  </Badge>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-green-600 whitespace-nowrap">
                    R$ {item.price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <Badge className={item.condition === 'novo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                    {item.condition.charAt(0).toUpperCase() + item.condition.slice(1)}
                  </Badge>
                </div>
              </div>
              
              {/* Descrição Completa */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 mb-2">{t('mkiDescription')}</h3>
                <p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
              </div>
              
              {/* Localização e Data */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {t('mkLocation')}
                  </p>
                  <p className="font-medium text-gray-800">{item.location}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" /> {t('mkPublishedOn')}
                  </p>
                  <p className="font-medium text-gray-800">
                    {new Date(item.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
              
              {/* Dias restantes */}
              {item.days_remaining !== null && item.days_remaining !== undefined && (
                <div className={`mb-6 p-3 rounded-lg ${item.days_remaining <= 7 ? 'bg-orange-50 border border-orange-200' : 'bg-blue-50 border border-blue-200'}`}>
                  <p className={`text-sm ${item.days_remaining <= 7 ? 'text-orange-700' : 'text-blue-700'}`}>
                    ⏱️ {t('mkAdExpiresIn')} <strong>{item.days_remaining} {t('mkDays')}</strong>
                    {item.days_remaining <= 7 && ` - ${t('mkRenewToKeepActive')}`}
                  </p>
                </div>
              )}
              
              {/* Vendedor */}
              {item.seller_info && (
                <div className="bg-green-50 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-green-800 mb-2">{t('mkiSeller')}</h3>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{item.seller_info.name}</span>
                    {item.seller_info.is_verified && <span className="text-green-600">✅</span>}
                    {item.seller_info.rating && (
                      <span className="text-sm bg-white px-2 py-1 rounded-full">
                        ⭐ {item.seller_info.rating.toFixed(1)} ({item.seller_info.rating_count})
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Contato */}
              <div className="bg-gray-100 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-700 mb-2">{t('mkiContact')}</h3>
                <p className="text-lg font-medium text-gray-800">{item.contact_info}</p>
              </div>
              
              {/* Botão Fechar */}
              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => setShowDetails(false)}
              >
                {t('close')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </Card>
  );
};

export default MarketplaceItemCard;
